import React, { useMemo } from 'react';
import moment from 'moment';
import { CHART_PRIMARY_COLOR, COMPARISON_PERIOD_COLOR } from './LifeCycleAnalyticsConstants';
import {
	buildMetricTooltipHtml,
	buildSeriesComparisonTooltipHtml,
	formatDurationShort,
	parseSafeFloat,
	roundToOneDecimalPlace,
} from './LifeCycleAnalyticsUtils';
import {
	COMPARISON_MODE,
	COMPARISON_SERIES_CURRENT,
	COMPARISON_SERIES_FIELD,
	COMPARISON_SERIES_PREVIOUS,
} from '../../utils/AnalyticsComparisonUtils';
import ComparisonLegend from '../analytics_comparison/ComparisonLegend';
import {
	AnalyticsChartActions,
	AnalyticsChartCard,
	AnalyticsChartHeader,
	MetricLineChart,
	pairComparisonByMonth,
} from '../analytics_charts';

const MetricChart = ({
	chartColor,
	chartMetric: _chartMetric,
	activeMetricConfig,
	chartMetricSummaryValue,
	chartMetricDeltaElement,
	monthlyMetricChartData,
	previousPeriodChartData,
	selectedPriorityCard,
	formatMetricAxisLabel,
	exportMetricChartCsv,
	comparisonState,
	onComparisonChange,
	selectedRange,
	comparisonRange,
	comparisonLoading,
	comparisonDisabled,
	comparisonDisabledReason,
}: any) => {
	const primaryColor = chartColor || CHART_PRIMARY_COLOR;
	const previousColor = COMPARISON_PERIOD_COLOR;
	const comparisonMode = comparisonState ? comparisonState.mode : COMPARISON_MODE.NONE;
	const hasComparison = comparisonMode !== COMPARISON_MODE.NONE;

	const chartData = useMemo(() => {
		const paired = pairComparisonByMonth({
			current: monthlyMetricChartData || [],
			previous: previousPeriodChartData || [],
			hasComparison,
			selectedRange,
			comparisonRange,
			buildPreviousRow: (prevRow, curRow: any) => ({
				...(prevRow || {}),
				monthLabel: curRow.monthLabel,
			}),
		});
		return [...paired.current, ...paired.previous];
	}, [
		monthlyMetricChartData,
		previousPeriodChartData,
		hasComparison,
		selectedRange,
		comparisonRange,
	]);

	if (!activeMetricConfig) {
		return null;
	}

	const formatTooltipValue = (raw: any) => {
		const num = parseSafeFloat(raw, NaN);
		if (isNaN(num)) return '-';
		return activeMetricConfig.isTimeBased
			? formatDurationShort(num)
			: roundToOneDecimalPlace(num) + '%';
	};

	const seriesColorMap: Record<string, string> = {
		[COMPARISON_SERIES_CURRENT]: primaryColor,
		[COMPARISON_SERIES_PREVIOUS]: previousColor,
	};

	const isCurrentItem = (item: any) =>
		!!(item && item.data && item.data[COMPARISON_SERIES_FIELD] === COMPARISON_SERIES_CURRENT);

	const renderComparisonTooltip = (title: string, items: any[]) => {
		if (!items || items.length === 0) return '';
		const rows = items
			.slice()
			.sort((a, b) => Number(isCurrentItem(b)) - Number(isCurrentItem(a)))
			.map((item) => {
				const monthFull = (item.data && item.data.monthFull) || title;
				return {
					label: monthFull,
					color: seriesColorMap[item.data[COMPARISON_SERIES_FIELD]],
					value: formatTooltipValue(item.data.metricRaw),
					dashed: !isCurrentItem(item),
				};
			});
		return buildSeriesComparisonTooltipHtml(rows);
	};

	const pointTooltipDefinition: [string, (...args: any[]) => { name: string; value: string }] = [
		'monthFull*metricValue*metricRaw',
		(monthFull: any, metricVal: any, metricRaw: any) => ({
			name: activeMetricConfig.label,
			value: buildMetricTooltipHtml(
				monthFull,
				activeMetricConfig.label,
				activeMetricConfig.isTimeBased
					? formatDurationShort(parseSafeFloat(metricRaw))
					: roundToOneDecimalPlace(parseSafeFloat(metricVal)) + '%',
				primaryColor
			),
		}),
	];

	const summaryValueFormatted = activeMetricConfig.isTimeBased
		? formatDurationShort(chartMetricSummaryValue)
		: roundToOneDecimalPlace(chartMetricSummaryValue) + '%';

	const selectedStart: moment.Moment | null = selectedRange ? selectedRange[0] : null;
	const selectedEnd: moment.Moment | null = selectedRange ? selectedRange[1] : null;
	const comparisonStart: moment.Moment | null = comparisonRange ? comparisonRange[0] : null;
	const comparisonEnd: moment.Moment | null = comparisonRange ? comparisonRange[1] : null;

	const headerPill = selectedPriorityCard
		? {
				label: selectedPriorityCard.woPriorityName,
				color: selectedPriorityCard.color,
				bgColor: selectedPriorityCard.bgColor,
		  }
		: null;

	const title = (
		<AnalyticsChartHeader
			pill={headerPill}
			label={activeMetricConfig.label}
			value={summaryValueFormatted}
			infoTooltip={activeMetricConfig.infoTooltip}
			delta={chartMetricDeltaElement}
		/>
	);

	const extra = (
		<AnalyticsChartActions
			comparison={
				onComparisonChange
					? {
							value: comparisonState || { mode: COMPARISON_MODE.NONE },
							onChange: onComparisonChange,
							selectedStart,
							selectedEnd,
							isTimeBased: true,
							loading: !!comparisonLoading,
							disabled: !!comparisonDisabled,
							disabledReason: comparisonDisabled ? comparisonDisabledReason : undefined,
					  }
					: undefined
			}
			onDownload={exportMetricChartCsv}
		/>
	);

	return (
		<AnalyticsChartCard title={title} extra={extra}>
			<div className="w-full min-w-0 print:!break-inside-avoid">
				<MetricLineChart
					data={chartData}
					hasComparison={hasComparison}
					currentColor={primaryColor}
					previousColor={previousColor}
					axisYTitle={activeMetricConfig.axisTitle}
					formatYAxisLabel={formatMetricAxisLabel}
					pointTooltipDefinition={pointTooltipDefinition}
					renderComparisonTooltip={renderComparisonTooltip}
				/>
				{hasComparison ? (
					<ComparisonLegend
						currentStart={selectedStart}
						currentEnd={selectedEnd}
						previousStart={comparisonStart}
						previousEnd={comparisonEnd}
						currentColor={primaryColor}
						previousColor={previousColor}
						mode={comparisonMode}
					/>
				) : (
					<div className="mt-3 h-5" aria-hidden="true" />
				)}
			</div>
		</AnalyticsChartCard>
	);
};

export default MetricChart;
